import type { PDFDocumentProxy } from 'pdfjs-dist';

import { uploadStudyDocument, type StudyDocument } from './studyApi';
import { STUDY_ANALYSIS_MAX_PAGES, uploadStudyDocumentPage } from './studyAnalysisApi';
import { rememberPageImage } from './studyAnalysisPageImages';
import type { PageRotation } from './studyPageGeometry';
import { preparePhotoPage, renderPdfPage, type EncodedPage } from './studyPageImage';

/**
 * Pages a learner is preparing for analysis (study-capture-analyse-prd.md §2, §3.2). A draft page
 * is either one photo (its own Study document, page 1) or one page of a PDF document. Uploads are
 * resumable: a page remembers the document it created, so "Try again" never adds a second copy.
 */

export interface DraftPage {
  id: string;
  kind: 'photo' | 'pdf';
  name: string;
  pageNumber: number;
  rotation: PageRotation;
  /** The photo as chosen, kept so a rotation can be applied again. */
  source: Blob | null;
  encoded: EncodedPage | null;
  previewUrl: string | null;
  blurry: boolean;
  dark: boolean;
  error: string | null;
  documentId: string | null;
  uploaded: boolean;
}

export const TOO_MANY_PHOTOS = `Only the first ${STUDY_ANALYSIS_MAX_PAGES} photos are used.`;

let draftSequence = 0;
function nextId() {
  draftSequence += 1;
  return `draft-${draftSequence}`;
}

export function previewUrlFor(blob: Blob | null): string | null {
  if (!blob || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(blob);
}

export function revokePreview(url: string | null): void {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

export function isPdfFile(file: Blob & { name?: string }): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? '');
}

/** Decodes and resizes one photo. A photo that cannot be read stays in the list with its message. */
export async function draftFromPhoto(
  source: Blob,
  name: string,
  rotation: PageRotation = 0,
  documentId: string | null = null,
): Promise<DraftPage> {
  const base = {
    id: nextId(),
    kind: 'photo' as const,
    name,
    pageNumber: 1,
    rotation,
    source,
    documentId,
    uploaded: false,
  };
  try {
    const photo = await preparePhotoPage(source, rotation, name);
    return {
      ...base,
      encoded: photo,
      previewUrl: previewUrlFor(photo.blob),
      blurry: photo.blurry,
      dark: photo.dark,
      error: null,
    };
  } catch (error) {
    return {
      ...base,
      encoded: null,
      previewUrl: null,
      blurry: false,
      dark: false,
      error: error instanceof Error ? error.message : "This photo can't be read.",
    };
  }
}

/** Applies a new rotation to a photo page. */
export async function rotateDraft(page: DraftPage, rotation: PageRotation): Promise<DraftPage> {
  if (page.kind !== 'photo' || !page.source) return page;
  const next = await draftFromPhoto(page.source, page.name, rotation, page.documentId);
  revokePreview(page.previewUrl);
  return { ...next, id: page.id };
}

export async function draftFromPdfPage(
  pdf: PDFDocumentProxy,
  pageNumber: number,
  name: string,
  documentId: string | null,
): Promise<DraftPage> {
  const base = {
    id: nextId(),
    kind: 'pdf' as const,
    name,
    pageNumber,
    rotation: 0 as PageRotation,
    source: null,
    blurry: false,
    dark: false,
    documentId,
    uploaded: false,
  };
  try {
    const encoded = await renderPdfPage(pdf, pageNumber);
    return { ...base, encoded, previewUrl: previewUrlFor(encoded.blob), error: null };
  } catch {
    return {
      ...base,
      encoded: null,
      previewUrl: null,
      error: `Page ${pageNumber} could not be prepared. Remove it or try again.`,
    };
  }
}

/** Default PDF pages: all of a short PDF, or the first 10 of a long one. */
export function defaultPdfPages(pageCount: number): number[] {
  return Array.from({ length: Math.min(pageCount, STUDY_ANALYSIS_MAX_PAGES) }, (_, i) => i + 1);
}

/** Toggles one page in a PDF selection, keeping at most 10 and the page order. */
export function togglePdfPage(selected: number[], pageNumber: number): number[] {
  if (selected.includes(pageNumber)) return selected.filter((page) => page !== pageNumber);
  if (selected.length >= STUDY_ANALYSIS_MAX_PAGES) return selected;
  return [...selected, pageNumber].sort((a, b) => a - b);
}

export function readyPages(pages: DraftPage[]): DraftPage[] {
  return pages.filter((page) => page.encoded !== null && page.error === null);
}

/**
 * Uploads the ready pages. Photos each become their own Study document; PDF pages are added to
 * `pdf.documentId` (created on the first call). Calls `onProgress` after each page and returns the
 * updated pages; on failure, the pages uploaded so far keep their document ids.
 */
export async function uploadDraftPages(input: {
  pages: DraftPage[];
  pdf: { file: File | null; documentId: string | null } | null;
  subjectKey?: string;
  onProgress: (pages: DraftPage[], pdfDocumentId: string | null) => void;
}): Promise<{ pages: DraftPage[]; pdfDocumentId: string | null }> {
  let pages = [...input.pages];
  let pdfDocumentId = input.pdf?.documentId ?? null;
  const update = (id: string, change: Partial<DraftPage>) => {
    pages = pages.map((page) => (page.id === id ? { ...page, ...change } : page));
    input.onProgress(pages, pdfDocumentId);
  };
  for (const page of readyPages(pages)) {
    if (page.uploaded) continue;
    let documentId = page.kind === 'pdf' ? pdfDocumentId : page.documentId;
    if (!documentId) {
      const file =
        page.kind === 'pdf'
          ? input.pdf?.file
          : page.source instanceof File
            ? page.source
            : new File([page.source ?? new Blob()], page.name, { type: page.source?.type });
      if (!file) throw new Error('The file is missing. Choose it again.');
      const created: StudyDocument = await uploadStudyDocument(file, input.subjectKey);
      documentId = created.id;
      if (page.kind === 'pdf') pdfDocumentId = documentId;
      update(page.id, { documentId });
    }
    await uploadStudyDocumentPage(documentId, page.pageNumber, page.encoded!.blob);
    rememberPageImage(documentId, page.pageNumber, page.encoded!.blob);
    update(page.id, { documentId, uploaded: true });
  }
  return { pages, pdfDocumentId };
}
